import { useLayoutEffect, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useApp } from '../../../app/AppContext.jsx'

import styles from './Popover.module.scss'

const OFFSET_X = 16  // px gap between cursor and popover
const OFFSET_Y = 12
const EDGE_GAP = 8   // min distance from container edge

/**
 * Popover — cursor-following floating container for desktop hover previews.
 *
 * Follows the mouse with a constant offset (right+below by default).
 * Flips horizontally/vertically when approaching container edges.
 * Renders hidden (`visibility:hidden`) until the first cursor position is known,
 * then measures its own size and positions correctly — no first-frame flash.
 *
 * Dismisses on scroll (wheel), arrow keys, and Escape.
 * `pointer-events: none` — clicks pass through to the underlying SVG layer;
 * on desktop, clicking the SVG path navigates to the product directly.
 *
 * Portal target: the current app instance portal host.
 *
 * @param {object}              props
 * @param {boolean}             props.isOpen
 * @param {{x:number,y:number}} [props.initialCursorPos] — seed position from the triggering mouseover
 * @param {function}            [props.onClose]           — called on Escape / scroll / arrow keys
 * @param {React.ReactNode}     props.children
 */
export function Popover({ isOpen, initialCursorPos, onClose, children }) {
  const { appRootId, portalHostId } = useApp()
  const popoverRef    = useRef(null)
  const initialPosRef = useRef(initialCursorPos)
  initialPosRef.current = initialCursorPos

  // ── Cursor-following positioning (imperative — no state, no re-renders) ───
  useLayoutEffect(() => {
    if (!isOpen) return
    const pop = popoverRef.current
    if (!pop) return

    const reposition = (cx, cy) => {
      const popW = pop.offsetWidth
      const popH = pop.offsetHeight
      if (!popW || !popH) return

      // Effective bounds = intersection of viewport and app container.
      // Viewport alone is wrong — WP navbar can overlay the top portion.
      // Container alone is wrong — it can extend beyond viewport when scrolled.
      const vw = window.innerWidth
      const vh = window.innerHeight
      const containerEl = document.getElementById(appRootId)
      const cr = containerEl?.getBoundingClientRect()

      const boundsTop    = Math.max(0,  cr?.top    ?? 0)
      const boundsLeft   = Math.max(0,  cr?.left   ?? 0)
      const boundsRight  = Math.min(vw, cr?.right  ?? vw)
      const boundsBottom = Math.min(vh, cr?.bottom ?? vh)

      let left = cx + OFFSET_X
      let top  = cy + OFFSET_Y

      // Flip X when popover would overflow right edge
      if (left + popW > boundsRight - EDGE_GAP) {
        left = cx - popW - OFFSET_X
      }
      // Flip Y when popover would overflow bottom edge
      if (top + popH > boundsBottom - EDGE_GAP) {
        top = cy - popH - OFFSET_Y
      }

      // Final clamp to effective bounds
      left = Math.max(boundsLeft + EDGE_GAP, Math.min(left, boundsRight  - popW - EDGE_GAP))
      top  = Math.max(boundsTop  + EDGE_GAP, Math.min(top,  boundsBottom - popH - EDGE_GAP))

      pop.style.top        = `${top}px`
      pop.style.left       = `${left}px`
      pop.style.visibility = 'visible'
    }

    // Seed from the triggering mouseover position (before first mousemove)
    const seed = initialPosRef.current
    if (seed) reposition(seed.x, seed.y)

    const onMove = (e) => reposition(e.clientX, e.clientY)
    document.addEventListener('mousemove', onMove)
    return () => document.removeEventListener('mousemove', onMove)
  }, [appRootId, isOpen])

  // ── Dismiss on scroll / wheel / arrow keys / Escape ───────────────────────
  useEffect(() => {
    if (!isOpen || !onClose) return
    const onWheel = () => onClose()
    const onKey   = (e) => {
      if (e.key === 'Escape' || e.key === 'ArrowUp' || e.key === 'ArrowDown') onClose()
    }
    document.addEventListener('wheel', onWheel, { passive: true })
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('wheel', onWheel)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const portalTarget = document.getElementById(portalHostId)
  if (!portalTarget) return null

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      role="tooltip"
      style={{ visibility: 'hidden' }}
    >
      {children}
    </div>,
    portalTarget,
  )
}
